import {
  Building2,
  Church,
  Mail,
  Upload,
  UserPlus,
} from "lucide-react";

import type { PlatformDashboardKpis } from "@/api/platform";

import { StatCard } from "./StatCard";

interface StatsRowProps {
  kpis: PlatformDashboardKpis;
}

export function StatsRow({
  kpis,
}: StatsRowProps) {
  return (
    <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
      <StatCard
        label="Pending Requests"
        value={kpis.pending_review}
        helperText={buildPendingRequestsHelperText(
          kpis
        )}
        icon={UserPlus}
        tone="indigo"
      />

      <StatCard
        label="Active Pilot Churches"
        value={kpis.active_churches}
        helperText={buildActiveChurchesHelperText(
          kpis
        )}
        icon={Church}
        tone="emerald"
      />

      <StatCard
        label="Pending Invitations"
        value={kpis.pending_invitations}
        helperText={buildPendingInvitationsHelperText(
          kpis
        )}
        icon={Mail}
        tone="amber"
      />

      <StatCard
        label="Churches Onboarding"
        value={kpis.churches_onboarding}
        helperText={buildOnboardingHelperText(
          kpis
        )}
        icon={Building2}
        tone="sky"
      />

      <StatCard
        label="Imports Running"
        value={kpis.imports_running}
        helperText={buildImportsHelperText(
          kpis
        )}
        icon={Upload}
        tone="rose"
      />
    </section>
  );
}

function buildPendingRequestsHelperText(
  kpis: PlatformDashboardKpis
): string {
  if (kpis.pending_review === 0) {
    return "No requests awaiting review";
  }

  if (kpis.pending_review === 1) {
    return "1 request awaiting review";
  }

  return `${kpis.pending_review} requests awaiting review`;
}

function buildActiveChurchesHelperText(
  kpis: PlatformDashboardKpis
): string {
  if (kpis.converted_requests === 0) {
    return "No churches activated yet";
  }

  if (kpis.converted_requests === 1) {
    return "1 converted pilot request";
  }

  return `${kpis.converted_requests} converted pilot requests`;
}

function buildPendingInvitationsHelperText(
  kpis: PlatformDashboardKpis
): string {
  if (kpis.pending_invitations === 0) {
    return "No invitations pending";
  }

  if (kpis.pending_invitations === 1) {
    return "1 invitation awaiting acceptance";
  }

  return `${kpis.pending_invitations} invitations awaiting acceptance`;
}

function buildOnboardingHelperText(
  kpis: PlatformDashboardKpis
): string {
  if (kpis.churches_onboarding === 0) {
    return "No churches onboarding";
  }

  if (kpis.churches_onboarding === 1) {
    return "1 approved church onboarding";
  }

  return `${kpis.churches_onboarding} approved churches onboarding`;
}

function buildImportsHelperText(
  kpis: PlatformDashboardKpis
): string {
  if (kpis.imports_running === 0) {
    return "No imports currently running";
  }

  if (kpis.imports_running === 1) {
    return "1 import currently running";
  }

  return `${kpis.imports_running} imports currently running`;
}